import { useState, useEffect, useCallback } from 'react'
import { getApiUrl, fetchWithAuth } from '@/api'

interface LockerItem {
  locker_id: number
  locker_number: number
  is_available: number
  reservation_id: number | null
  reserved_member_id: number | null
  start_date: string | null
  end_date: string | null
  status: number | null
}

const STATUS_STYLE: Record<number, { label: string; cls: string }> = {
  0: { label: '확인대기', cls: 'bg-[#fff8e1] text-[#e67e22]' },
  1: { label: '확인완료', cls: 'bg-[#e8f5e9] text-[#388e3c]' },
  2: { label: '신청취소', cls: 'bg-[#f5f5f5] text-[#9e9e9e]' },
  3: { label: '이용종료', cls: 'bg-[#e3f2fd] text-[#1976d2]' },
}

export default function AdminLockerManageTab() {
  const [lockers, setLockers] = useState<LockerItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState('')
  const [settingSaving, setSettingSaving] = useState(false)
  const [clearingId, setClearingId] = useState<number | null>(null)

  const fetchLockers = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/locker/available'))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setLockers(data.lockers)
      setTotal(data.total)
      setTotalCount(String(data.total))
    } catch {
      alert('금고 목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLockers()
  }, [fetchLockers])

  const handleSettingSave = async () => {
    const n = Number(totalCount)
    if (!totalCount || isNaN(n) || n < 1) { alert('1 이상의 숫자를 입력해주세요.'); return }
    if (!window.confirm(`금고 총 개수를 ${n}개로 설정하시겠습니까?`)) return
    setSettingSaving(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/admin/locker/settings'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ total_count: n }),
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '설정 실패') }
      alert('금고 개수가 설정되었습니다.')
      fetchLockers()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '설정에 실패했습니다.')
    } finally {
      setSettingSaving(false)
    }
  }

  const handleClear = async (lockerId: number, lockerNumber: number) => {
    if (!window.confirm(`${lockerNumber}번 금고를 비우겠습니까?`)) return
    setClearingId(lockerId)
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/admin/locker/${lockerId}/clear`),
        { method: 'PATCH' }
      )
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchLockers()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '비움 처리에 실패했습니다.')
    } finally {
      setClearingId(null)
    }
  }

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })

  const occupied = lockers.filter(l => l.is_available === 0).length
  const available = lockers.filter(l => l.is_available === 1).length

  return (
    <div className="flex flex-col gap-4">
      {/* 설정 카드 */}
      <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-5 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
        <h2 className="text-[13px] font-bold text-[#5a3d32] mb-4">금고 개수 설정</h2>
        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-[#5a3d32] font-medium">총 금고 개수</label>
            <input
              type="number" min={1}
              value={totalCount}
              onChange={e => setTotalCount(e.target.value)}
              placeholder="예: 20"
              className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[120px] bg-white"
            />
          </div>
          <button
            onClick={handleSettingSave}
            disabled={settingSaving}
            className="bg-[#887267] text-white text-[13px] font-semibold px-5 py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50 h-[38px]"
          >
            {settingSaving ? '저장 중...' : '저장'}
          </button>
          <div className="flex items-center gap-4 ml-4">
            <span className="text-[12px] text-gray-400">
              전체 <span className="text-[#2a1f1a] font-semibold">{total}</span>개
            </span>
            <span className="text-[12px] text-gray-400">
              사용중 <span className="text-[#e67e22] font-semibold">{occupied}</span>개
            </span>
            <span className="text-[12px] text-gray-400">
              비어있음 <span className="text-[#388e3c] font-semibold">{available}</span>개
            </span>
          </div>
        </div>
      </div>

      {/* 금고 현황 그리드 */}
      <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-5 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
        <h2 className="text-[13px] font-bold text-[#5a3d32] mb-4">금고 현황</h2>

        {loading ? (
          <div className="min-h-[200px] flex items-center justify-center text-gray-300 text-[13px]">
            불러오는 중...
          </div>
        ) : lockers.length === 0 ? (
          <div className="min-h-[200px] flex items-center justify-center text-gray-300 text-[13px]">
            등록된 금고가 없습니다.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {lockers.map(locker => {
              const isOccupied = locker.is_available === 0
              const st = locker.status !== null ? STATUS_STYLE[locker.status] : null
              const isClearing = clearingId === locker.locker_id

              return (
                <div
                  key={locker.locker_id}
                  className={`rounded-xl border px-4 py-3 flex flex-col gap-2 transition-colors ${
                    isOccupied
                      ? 'border-[#f0d9ce] bg-[#fff8f6]'
                      : 'border-[#d8eedb] bg-[#f6fdf7]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[14px] font-bold text-[#2a1f1a]">
                      🔒 {locker.locker_number}번
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      isOccupied ? 'bg-[#ffe0cc] text-[#b54f00]' : 'bg-[#c8e6c9] text-[#2e7d32]'
                    }`}>
                      {isOccupied ? '사용중' : '비어있음'}
                    </span>
                  </div>

                  {isOccupied && (
                    <div className="flex flex-col gap-0.5">
                      {st && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium self-start ${st.cls}`}>
                          {st.label}
                        </span>
                      )}
                      {locker.start_date && (
                        <span className="text-[11px] text-gray-400">
                          {formatDate(locker.start_date)} ~
                        </span>
                      )}
                      {locker.end_date && (
                        <span className="text-[11px] text-gray-400">
                          {formatDate(locker.end_date)}
                        </span>
                      )}
                    </div>
                  )}

                  {isOccupied && locker.status === 1 && (
                    <button
                      onClick={() => handleClear(locker.locker_id, locker.locker_number)}
                      disabled={isClearing}
                      className="mt-auto text-[11px] text-red-400 hover:text-red-600 border border-red-100 rounded-lg px-2 py-1 bg-white cursor-pointer transition-colors disabled:opacity-50"
                    >
                      {isClearing ? '처리 중...' : '🗑 비우기'}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}